import React, { useState } from 'react';
import { Calendar, ArrowUpRight, Search, Filter, ExternalLink } from 'lucide-react';
import { content, getText } from '../data/content';
import { theme, combineClasses, getCardStyle } from '../theme/theme';
import Modal from '../components/Modal';

/**
 * ============================================================================
 * 页面：新闻动态 - News.jsx
 * ============================================================================
 *
 * 【作用】新闻列表页：标题区、分类筛选、搜索、新闻卡片；点击卡片打开详情弹窗（正文、作者、链接、日期）。分类与 content-news 中 category 一致。
 * 【如何修改】新闻数据在 src/data/content-news.js 的 items 数组；categories 需与 item.category 取值一致（All/Research/Award/Team/News）。
 *
 * 【参数说明】props: lang, themeMode。内部状态：filter（分类）、searchTerm（搜索）、selectedNews（当前选中新闻）。
 */
const NewsView = ({ lang, themeMode = "dark" }) => {
  const isLight = themeMode === "light";
  const [filter, setFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNews, setSelectedNews] = useState(null);

  const t = content.news;
  const categories = ['All', 'Research', 'Award', 'Team', 'News'];

  const filteredNews = t.items.filter(item => {
    const matchesFilter = filter === 'All' || item.category === filter;
    const matchesSearch = searchTerm === '' || 
      item.title[lang].toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.summary[lang].toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // 打开新闻详情模态框
  const openNewsModal = (item) => {
    setSelectedNews(item);
  };

  // 关闭模态框
  const closeModal = () => {
    setSelectedNews(null);
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Research': 'text-cyan-400 bg-cyan-400/10 border-cyan-400/20',
      'Award': 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
      'Team': 'text-purple-400 bg-purple-400/10 border-purple-400/20',
      'Publication': 'text-pink-400 bg-pink-400/10 border-pink-400/20'
    };
    return colors[category] || 'text-gray-400 bg-gray-400/10 border-gray-400/20';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(lang === 'cn' ? 'zh-CN' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className={combineClasses(
      "min-h-screen pt-8 pb-20 px-4",
      theme.layout.container,
      theme.animations.fadeIn
    )}>
      {/* 页面标题 */}
      <div className="text-center mb-16">
        <h2 className={combineClasses(
          "text-4xl md:text-6xl font-bold mb-4",
          isLight ? "text-gray-900" : "text-white"
        )}>
          {getText('news.title', lang)}
        </h2>
        <p className={combineClasses(
          "text-lg max-w-2xl mx-auto",
          isLight ? "text-gray-600" : "text-gray-400"
        )}>
          {getText('news.subtitle', lang)}
        </p>
      </div>

      {/* 搜索和过滤器 */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-12 gap-6">
        {/* 搜索框 */}
        <div className="relative w-full md:w-96">
          <Search className={combineClasses(
            "absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5",
            isLight ? "text-gray-500" : "text-gray-400"
          )} />
          <input
            type="text"
            placeholder={lang === 'cn' ? '搜索新闻...' : 'Search news...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={combineClasses(
              "w-full pl-10 pr-4 py-3 border rounded-full focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 backdrop-blur-sm transition-colors",
              isLight 
                ? "bg-gray-50 border-gray-200 text-gray-900" 
                : "bg-white/5 border-white/10 text-gray-300"
            )}
          />
        </div>

        {/* 分类过滤器 */}
        <div className="flex flex-wrap gap-2">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={combineClasses(
                "px-4 py-2 rounded-lg text-sm font-bold transition-all",
                filter === cat
                  ? isLight 
                    ? "bg-gray-900 text-white" 
                    : "bg-white text-black"
                  : isLight 
                    ? "bg-gray-100 text-gray-600 hover:bg-gray-200" 
                    : "bg-white/5 text-gray-400 hover:bg-white/10"
              )}
            >
              {cat === 'All' ? (lang === 'cn' ? '全部' : 'All') : cat}
            </button>
          ))}
        </div>
      </div>

      {/* 新闻列表 */}
      <div className="space-y-6">
        {filteredNews.map((item, index) => (
          <article
            key={item.id}
            className={combineClasses(
              getCardStyle(themeMode),
              "group cursor-pointer transform hover:-translate-y-1 border"
            )}
            onClick={() => openNewsModal(item)}
          >
            <div className="p-8">
              <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                <div className="flex-1">
                  {/* 日期和分类 */}
                  <div className="flex items-center gap-4 mb-4">
                    <div className={combineClasses(
                      "flex items-center gap-2 text-sm",
                      isLight ? "text-gray-600" : "text-gray-400"
                    )}>
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(item.date)}</span>
                    </div>
                    <span className={combineClasses(
                      "px-3 py-1 rounded-full text-xs font-bold border",
                      getCategoryColor(item.category)
                    )}>
                      {item.category}
                    </span>
                    {item.featured && (
                      <span className="px-2 py-1 bg-gradient-to-r from-cyan-500 to-purple-500 text-white text-xs font-bold rounded-full">
                        {lang === 'cn' ? '精选' : 'Featured'}
                      </span>
                    )}
                  </div>

                  {/* 标题 */}
                  <h3 className={combineClasses(
                    "text-2xl font-bold mb-3 transition-colors",
                    isLight 
                      ? "text-gray-900 group-hover:text-cyan-600" 
                      : "text-white group-hover:text-cyan-300"
                  )}>
                    {item.title[lang]}
                  </h3>

                  {/* 摘要 */}
                  <p className={combineClasses(
                    "leading-relaxed",
                    isLight ? "text-gray-700" : "text-gray-300"
                  )}>
                    {item.summary[lang]}
                  </p>
                </div>

                {/* 阅读更多按钮 */}
                <div className="flex-shrink-0">
                  <button 
                    className={combineClasses(
                      "p-3 rounded-full transition-all group-hover:scale-110",
                      isLight 
                        ? "bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900" 
                        : "bg-white/5 hover:bg-white hover:text-black text-gray-400"
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      openNewsModal(item);
                    }}
                  >
                    <ArrowUpRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </article>
        ))}
      </div>

      {/* 无结果提示 */}
      {filteredNews.length === 0 && (
        <div className="text-center py-16">
          <div className={combineClasses(
            "text-lg mb-4",
            isLight ? "text-gray-600" : "text-gray-400"
          )}>
            {lang === 'cn' ? '没有找到相关新闻' : 'No news found'}
          </div>
          <button
            onClick={() => {
              setFilter('All');
              setSearchTerm('');
            }}
            className={isLight ? theme.buttonsLight.secondary : theme.buttons.secondary}
          >
            {lang === 'cn' ? '清除筛选' : 'Clear Filters'}
          </button>
        </div>
      )}

      {/* 新闻详情模态框 */}
      <Modal
        isOpen={!!selectedNews}
        onClose={closeModal}
        title={selectedNews ? (selectedNews.title[lang] || selectedNews.title) : ''}
        size="lg"
        themeMode={themeMode}
      >
        {selectedNews && selectedNews.details && (
          <div className="space-y-6">
            {/* 新闻图片 */}
            {selectedNews.image && (
              <div className={combineClasses(
                "w-full h-64 rounded-xl overflow-hidden border",
                isLight ? "border-gray-200" : "border-white/10"
              )}>
                <img 
                  src={selectedNews.image} 
                  alt={selectedNews.title[lang] || selectedNews.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}

            {/* 新闻元信息 */}
            <div className={combineClasses(
              "flex items-center gap-4 pb-4 border-b",
              isLight ? "border-gray-200" : "border-white/10"
            )}>
              <div className={combineClasses(
                "flex items-center gap-2 text-sm",
                isLight ? "text-gray-600" : "text-gray-400"
              )}>
                <Calendar className="w-4 h-4" />
                <span>{formatDate(selectedNews.date)}</span>
              </div>
              <span className={combineClasses(
                "px-3 py-1 rounded-full text-xs font-bold border",
                getCategoryColor(selectedNews.category)
              )}>
                {selectedNews.category}
              </span>
              {selectedNews.featured && (
                <span className="px-2 py-1 bg-gradient-to-r from-cyan-500 to-purple-500 text-white text-xs font-bold rounded-full">
                  {lang === 'cn' ? '精选' : 'Featured'}
                </span>
              )}
            </div>

            {/* 弹窗正文：先 summary，再可选 abstract，最后 fullText；作者与链接在文末 */}
            <div className="space-y-4">
              {/* 摘要：与列表一致，用 item.summary */}
              <div>
                <h4 className={combineClasses(
                  "text-sm font-bold text-cyan-400 uppercase tracking-wider mb-2"
                )}>
                  {lang === 'cn' ? '摘要' : 'Summary'}
                </h4>
                <p className={combineClasses(
                  "leading-relaxed",
                  isLight ? "text-gray-700" : "text-gray-300"
                )}>
                  {selectedNews.summary[lang]}
                </p>
              </div>

              {/* 可选 abstract：在 details 中配置，平时注释掉则不显示 */}
              {selectedNews.details[lang]?.abstract && (
                <div>
                  <h4 className={combineClasses(
                    "text-sm font-bold text-cyan-400 uppercase tracking-wider mb-2"
                  )}>
                    {lang === 'cn' ? '导语' : 'Abstract'}
                  </h4>
                  <p className={combineClasses(
                    "leading-relaxed",
                    isLight ? "text-gray-700" : "text-gray-300"
                  )}>
                    {selectedNews.details[lang].abstract}
                  </p>
                </div>
              )}

              {/* 详细正文 */}
              {selectedNews.details[lang]?.fullText && (
                <div>
                  <h4 className={combineClasses(
                    "text-sm font-bold text-cyan-400 uppercase tracking-wider mb-2"
                  )}>
                    {lang === 'cn' ? '详细内容' : 'Full Text'}
                  </h4>
                  <p className={combineClasses(
                    "leading-relaxed whitespace-pre-line",
                    isLight ? "text-gray-700" : "text-gray-300"
                  )}>
                    {selectedNews.details[lang].fullText}
                  </p>
                </div>
              )}

              {selectedNews.details[lang]?.author && (
                <div>
                  <h4 className={combineClasses(
                    "text-sm font-bold text-cyan-400 uppercase tracking-wider mb-2"
                  )}>
                    {lang === 'cn' ? '作者' : 'Author'}
                  </h4>
                  <p className={combineClasses(
                    "leading-relaxed",
                    isLight ? "text-gray-600" : "text-gray-300"
                  )}>
                    {selectedNews.details[lang].author}
                  </p>
                </div>
              )}

              {selectedNews.details[lang]?.link && (
                <div className={combineClasses(
                  "pt-4 border-t",
                  isLight ? "border-gray-200" : "border-white/10"
                )}>
                  <a
                    href={selectedNews.details[lang].link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={combineClasses(
                      "inline-flex items-center gap-2 font-semibold transition-colors",
                      isLight ? "text-cyan-600 hover:text-gray-900" : "text-cyan-400 hover:text-white"
                    )}
                  >
                    <ExternalLink className="w-4 h-4" />
                    {lang === 'cn' ? '查看原文' : 'Read Full Article'}
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default NewsView;
